/* import __COLOCATED_TEMPLATE__ from './steps.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import { type SalesforceSetupData } from 'embercom/objects/standalone/salesforce';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'embercom/services/intl';

const FULL_PERMISSION_SET_PACKAGE_ID = '04tgK0000001kyT';
const CASES_PERMISSION_SET_PACKAGE_ID = '04tgK0000004HOr';

interface Args {
  hideAssignStep?: boolean;
}

export default class PermissionsSteps extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;

  get salesforcePlatform() {
    return this.finStandaloneService.salesforcePlatform;
  }

  get salesforceConfig(): SalesforceConfiguration {
    return this.salesforcePlatform.salesforceConfig!;
  }

  get salesforceSetupData(): SalesforceSetupData {
    return this.salesforcePlatform.salesforceSetupData;
  }

  get isDisabled() {
    return this.isLoading || this.isComplete || this.salesforceConfig?.isPendingSetup || false;
  }

  get isComplete() {
    return this.salesforceSetupData.hasPermissionSet;
  }

  get isLoading() {
    return (
      this.salesforcePlatform.salesforceDataLoader.isLoadingSalesforceSetupData ||
      (this.salesforcePlatform.salesforceDataLoader.isLoadingSalesforcePermissions &&
        !this.salesforceSetupData.hasPermissionSet)
    );
  }

  get installationSteps() {
    let prefix = 'standalone.salesforce.setup.permissions.steps';
    let steps = [
      { label: this.intl.t(`${prefix}.step-1`) },
      { label: this.intl.t(`${prefix}.step-2`, { htmlSafe: true }) },
      { label: this.intl.t(`${prefix}.step-3`, { htmlSafe: true }) },
      { label: this.intl.t(`${prefix}.step-4`) },
    ];
    if (!this.args.hideAssignStep) {
      steps.push({ label: this.intl.t(`${prefix}.step-5`, { htmlSafe: true }) });
    }
    return steps;
  }

  @action
  openFullPermissionSetUrl() {
    safeWindowOpen(this.fullPermissionSetUrl, '_blank');
  }

  get fullPermissionSetUrl() {
    return this.buildPermissionSetUrl(FULL_PERMISSION_SET_PACKAGE_ID);
  }

  get casesOnlyPermissionSetUrl() {
    return this.buildPermissionSetUrl(CASES_PERMISSION_SET_PACKAGE_ID);
  }

  buildPermissionSetUrl(permissionSetId: string) {
    let loginSubdomain = this.salesforceConfig.isSandbox ? 'test' : 'login';
    return `https://${loginSubdomain}.salesforce.com/packaging/installPackage.apexp?p0=${permissionSetId}`;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::Setup::Permissions::Steps': typeof PermissionsSteps;
  }
}
